import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Slider, TextField } from "@mui/material";
import { rotationMarks } from "../constants";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { ExpandMore, OpenWith } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  removeElement,
  updateElement,
  updateMultiSelection,
} from "../../../reducers/configurator/configuratorSlice";
import { Create, Clear } from "@mui/icons-material";
import {
  ArrowBack,
  ArrowForward,
  ArrowDownward,
  ArrowUpward,
} from "@mui/icons-material";
import clsx from "clsx";

const PREFIX = "GroundLogoSettings";

const classes = {
  root: `${PREFIX}-root`,
  iconImageContainer: `${PREFIX}-iconImageContainer`,
  imgStyle: `${PREFIX}-imgStyle`,
  sliderPadding: `${PREFIX}-sliderPadding`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  deleteImageIcon: `${PREFIX}-deleteImageIcon`,
  accordionStyle: `${PREFIX}-accordionStyle`,
  textAndIconStyle: `${PREFIX}-textAndIconStyle`,
  iconsContainer: `${PREFIX}-iconsContainer`,
  groundLogoMove: `${PREFIX}-groundLogoMove`,
  summaryStyle: `${PREFIX}-summaryStyle`,
  iconButtonStyle: `${PREFIX}-iconButtonStyle`,
  selectedOffset: `${PREFIX}-selectedOffset`,
  marginRight: `${PREFIX}-marginRight`,
};

const GroundLogoSettingsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      width: "100%",
      position: "relative",
      marginTop: "0.5rem",
    },
    [`& .${classes.iconImageContainer}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      paddingLeft: "2rem",
    },
    [`& .${classes.imgStyle}`]: {
      maxHeight: "100px",
      objectFit: "contain",
      borderRadius: "10px",
    },
    [`& .${classes.editImageIcon}`]: {},
    [`& .${classes.deleteImageIcon}`]: {},
    [`& .${classes.summaryStyle}`]: {
      "& > div": {
        position: "relative",
      },
    },
    [`& .${classes.sliderPadding}`]: {
      padding: "0.5rem",
      "& .MuiSlider-thumb": {
        marginTop: "0",
        marginLeft: "0",
      },
      "& > span": {
        "@media (pointer: coarse)": {
          padding: "13px 0",
        },
      },
      "& > span > span": {
        "&.MuiSlider-markLabel": {
          "@media (pointer: coarse)": {
            top: "30px",
          },
        },
      },
    },
    [`& .${classes.accordionStyle}`]: {
      width: "100%",
      marginBottom: "0.25rem !important",
      backgroundColor: "#303030",
    },
    [`& .${classes.textAndIconStyle}`]: {
      color: "#D3AF5F",
    },
    [`& .${classes.iconsContainer}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
    },
    [`& .${classes.groundLogoMove}`]: {
      padding: "0",
      position: "absolute",
      right: "8px",
    },
    [`& .${classes.iconButtonStyle}`]: {
      marginRight: "0.5rem !important",
      padding: 0,
    },
    [`& .${classes.selectedOffset}`]: {
      backgroundColor: theme.palette.primary.main,
      "& > svg": {
        fill: "#303030",
      },
    },
    [`& .${classes.marginRight}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        marginRight: "0",
      },
      marginRight: "0.5rem",
    },
  };
});

const GroundLogoSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const handleAddGroundLogoToTile = (event) => {
    dispatch(
      removeElement({
        layer: "ground",
        data: {
          file: event.target.files[0],
          loaded: false,
          rotation: 0,
          scale: 1,
        },
        tile: currentTile,
      })
    );
  };

  const handleDeleteGroundLogoInTile = (event) => {
    dispatch(
      updateElement({
        layer: "ground",
        data: {
          ...myTile,
          ground: { file: null, imageUrl: null, scale: 1, rotation: 0 },
        },
        tile: currentTile,
      })
    );
  };

  const centerSpawn = (key, direction) => {
    const myData = { ...myTile.ground };
    if (myTile.ground[key] === direction) {
      delete myData[key];
    } else {
      myData[key] = direction;
    }
    dispatch(
      updateElement({
        layer: "ground",
        data: myData,
        tile: currentTile,
      })
    );
  };

  if (myTile?.ground?.file || myTile?.ground?.imageUrl)
    return (
      <GroundLogoSettingsContainer sx={{ width: "100%" }}>
        <Accordion className={classes.accordionStyle}>
          <AccordionSummary
            className={classes.summaryStyle}
            expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
            aria-controls="ground-logo-settings"
            id="ground-logo-accordion"
            sx={{
              "&.Mui-expanded": {
                borderBottom: "1px solid grey",
              },
            }}
          >
            <Typography className={classes.textAndIconStyle}>
              {t("configurator.groundLogoSettings")}
            </Typography>
            <IconButton
              className={classes.groundLogoMove}
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  updateMultiSelection({
                    data: myTile["ground"],
                    move: true,
                    active: true,
                    tile: myTile,
                    oldLayer: "ground",
                    layer: "ground",
                    x: 0,
                    y: 0,
                  })
                );
              }}
            >
              <OpenWith />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.root}>
              <div className={classes.iconImageContainer}>
                <img
                  alt="ground logo"
                  src={
                    myTile.ground.file
                      ? URL.createObjectURL(myTile.ground.file)
                      : myTile.ground.imageUrl
                  }
                  className={classes.imgStyle}
                />
                <div className={classes.iconsContainer}>
                  <IconButton
                    color="primary"
                    aria-label="clear ground logo"
                    className={classes.deleteImageIcon}
                    component="span"
                    onClick={(e) => {
                      handleDeleteGroundLogoInTile(e);
                    }}
                  >
                    <Clear />
                  </IconButton>
                  <div className={classes.editImageIcon}>
                    <input
                      type="file"
                      accept="image/*"
                      name={`edit-ground-logo-${currentTile.row}-${currentTile.column}`}
                      id={`edit-ground-logo-${currentTile.row}-${currentTile.column}`}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleAddGroundLogoToTile(e);
                      }}
                    />
                    <label
                      htmlFor={`edit-ground-logo-${currentTile.row}-${currentTile.column}`}
                    >
                      <IconButton
                        color="primary"
                        aria-label="upload ground logo"
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Create />
                      </IconButton>
                    </label>
                  </div>
                </div>
              </div>
              <div className={classes.sliderPadding}>
                <Slider
                  aria-label="Angle"
                  size="small"
                  key={`slide-rotation-${
                    myTile.ground?.file
                      ? myTile.ground.file.name
                      : myTile.ground.imageUrl
                  }`}
                  value={myTile.ground.rotation}
                  onChange={(e) =>
                    dispatch(
                      updateElement({
                        layer: "ground",
                        data: { ...myTile.ground, rotation: e.target.value },
                        tile: currentTile,
                      })
                    )
                  }
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={90}
                  marks={rotationMarks}
                  min={0}
                  max={270}
                />
              </div>

              {/* <Slider
                  aria-label="Scale"
                  size="small"
                  ey={`slide-scale-${
                    myTile.ground?.file
                      ? myTile.ground.file.name
                      : myTile.ground.imageUrl
                  }`}
                  value={myTile.ground.scale}
                  onChange={(e) =>
                    dispatch(
                      updateElement({
                        layer: "ground",
                        data: { ...myTile.ground, scale: e.target.value },
                        tile: currentTile,
                      })
                    )
                  }
                  getAriaValueText={(value) => `${value}`}
                  valueLabelDisplay="auto"
                  step={0.5}
                  marks={scaleMarks}
                  min={0.5}
                  max={10}
                /> */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  marginTop: "1rem",
                }}
              >
                <TextField
                  label={t("configurator.sizeX")}
                  type="number"
                  sx={{ width: "70px" }}
                  value={myTile.ground.scale || 1}
                  className={classes.marginBottom}
                  onChange={(e) =>
                    dispatch(
                      updateElement({
                        layer: "ground",
                        data: { ...myTile.ground, scale: e.target.value },
                        tile: currentTile,
                      })
                    )
                  }
                  variant={"outlined"}
                  InputProps={{
                    inputProps: { min: 0.5, max: 10, step: 0.5 },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {/* <Slider
                  aria-label="ScaleY"
                  size="small"
                  ey={`slide-scale-Y-${
                    myTile.ground?.file
                      ? myTile.ground.file.name
                      : myTile.ground.imageUrl
                  }`}
                  value={myTile.ground.scaleY}
                  onChange={(e) =>
                    dispatch(
                      updateElement({
                        layer: "ground",
                        data: { ...myTile.ground, scaleY: e.target.value },
                        tile: currentTile,
                      })
                    )
                  }
                  getAriaValueText={(value) => `${value}`}
                  valueLabelDisplay="auto"
                  step={0.5}
                  marks={scaleMarks}
                  min={0.5}
                  max={10}
                /> */}
                <TextField
                  label={t("configurator.sizeY")}
                  type="number"
                  sx={{ width: "70px" }}
                  value={myTile.ground.scaleY || myTile.ground.scale}
                  className={classes.marginBottom}
                  onChange={(e) =>
                    dispatch(
                      updateElement({
                        layer: "ground",
                        data: { ...myTile.ground, scaleY: e.target.value },
                        tile: currentTile,
                      })
                    )
                  }
                  variant={"outlined"}
                  InputProps={{
                    inputProps: { min: 0.5, max: 10, step: 0.5 },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    marginLeft: "0.5rem",
                  }}
                >
                  <Typography variant="body2" sx={{ marginBottom: "0.25rem" }}>
                    {t("configurator.offsetTitle")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <IconButton
                      color="primary"
                      aria-label="offset spawn top"
                      component="span"
                      className={clsx(
                        classes.iconButtonStyle,
                        classes.marginRight,
                        myTile.ground.offsetY === "top"
                          ? classes.selectedOffset
                          : ""
                      )}
                      onClick={() => {
                        centerSpawn("offsetY", "top");
                      }}
                    >
                      <ArrowUpward fontSize="medium" />
                    </IconButton>
                    <IconButton
                      color="primary"
                      aria-label="offset span bottom"
                      component="span"
                      className={clsx(
                        classes.iconButtonStyle,
                        classes.marginRight,
                        myTile.ground.offsetY === "bottom" &&
                          classes.selectedOffset
                      )}
                      onClick={() => centerSpawn("offsetY", "bottom")}
                    >
                      <ArrowDownward fontSize="medium" />
                    </IconButton>
                    <IconButton
                      color="primary"
                      aria-label="offset span right"
                      component="span"
                      className={clsx(
                        classes.iconButtonStyle,
                        classes.marginRight,
                        myTile.ground.offset === "right" &&
                          classes.selectedOffset
                      )}
                      onClick={() => centerSpawn("offset", "right")}
                    >
                      <ArrowForward fontSize="medium" />
                    </IconButton>

                    <IconButton
                      color="primary"
                      aria-label="offset span left"
                      component="span"
                      className={clsx(
                        classes.iconButtonStyle,
                        classes.marginRight,
                        myTile.ground.offset === "left" &&
                          classes.selectedOffset
                      )}
                      onClick={() => centerSpawn("offset", "left")}
                    >
                      <ArrowBack fontSize="medium" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </div>
          </AccordionDetails>
        </Accordion>
      </GroundLogoSettingsContainer>
    );
  else return null;
};

export default GroundLogoSettings;
